<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div
      :class="$style.container"
      :style="style.container"
    >
      <div
        v-if="!disableFirstPageButton"
        :disabled="disabled || !previousPageExists"
        @click="selectPage(firstPage)"
        :class="$style.firstPageButton"
        :style="style.firstPageButton"
      >
        <slot name="first-page-button">
          <div>
            &#171;
          </div>
        </slot>
      </div>
      <div
        v-if="!disablePreviousPageButton"
        :disabled="disabled || !previousPageExists"
        @click="selectPage(previousPage)"
        :class="$style.previousPageButton"
        :style="style.previousPageButton"
      >
        <slot name="previous-page-button">
          <div>
            &#8249;
          </div>
        </slot>
      </div>
      <div
        v-for="(displayedPage, index) in displayedPages"
        :key="index"
        :style="style.pages"
      >
        <div
          v-if="displayedPage !== null"
          :disabled="disabled || displayedPage === null"
          :selected="displayedPage === page"
          @click="selectPage(displayedPage)"
          :class="$style.pageButton"
          :style="style.pageButton"
        >
          <slot
            name="page-button"
            :page="displayedPage"
          >
            {{ displayedPage }}
          </slot>
        </div>
        <div
          v-else
          :disabled="disabled"
          :class="$style.ellipsis"
          :style="style.ellipsis"
        >
          <slot name="ellipsis">
            &#183;&#183;&#183;
          </slot>
        </div>
      </div>
      <div
        v-if="!disableNextPageButton"
        :disabled="disabled || !nextPageExists"
        @click="selectPage(nextPage)"
        :class="$style.nextPageButton"
        :style="style.nextPageButton"
      >
        <slot name="next-page-button">
          <div>
            &#8250;
          </div>
        </slot>
      </div>
      <div
        v-if="!disableLastPageButton"
        :disabled="disabled || !nextPageExists"
        @click="selectPage(lastPage)"
        :class="$style.lastPageButton"
        :style="style.lastPageButton"
      >
        <slot name="last-page-button">
          <div>
            &#187;
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'

  export default {
    name: 'BasePagination',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change',
    },
    props: {
      vmodelValue: {
        type: [Number, Object],
        required: false,
        default: null,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableFirstPageButton: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableLastPageButton: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableNextPageButton: {
        type: Boolean,
        required: false,
        default: false,
      },
      disablePreviousPageButton: {
        type: Boolean,
        required: false,
        default: false,
      },
      fixFirstPageButton: {
        type: Boolean,
        required: false,
        default: false,
      },
      fixLastPageButton: {
        type: Boolean,
        required: false,
        default: false,
      },
      minimumDisplayedPageCount: {
        type: Number,
        required: false,
        default: 3,
      },
      pageCount: {
        type: Number,
        required: false,
        default: 1,
      },
      pageStartsFromZero: {
        type: Boolean,
        required: false,
        default: false,
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        page: this.vmodelValue,
        style: {
          container: {
            alignItems: 'center',
            display: 'flex',
          },
          ellipsis: {},
          firstPageButton: {},
          lastPageButton: {},
          nextPageButton: {},
          pageButton: {},
          pages: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          },
          previousPageButton: {},
        },
      };
    },
    created: function () {
      const proc = new CssStyleProcessor(
        '--v2-base-pagination',
        ['selected'],
      );

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.firstPageButton,
        proc.getExtendedStyle(
          this.styleConfig.firstPageButton,
          'first-page-button',
        ),
      );
      Object.assign(
        this.style.lastPageButton,
        proc.getExtendedStyle(
          this.styleConfig.lastPageButton,
          'last-page-button',
        ),
      );
      Object.assign(
        this.style.nextPageButton,
        proc.getExtendedStyle(
          this.styleConfig.nextPageButton,
          'next-page-button',
        ),
      );
      Object.assign(
        this.style.pageButton,
        proc.getExtendedStyle(
          this.styleConfig.pageButton,
          'page-button',
        ),
      );
      Object.assign(
        this.style.previousPageButton,
        proc.getExtendedStyle(
          this.styleConfig.previousPageButton,
          'previous-page-button',
        ),
      );


      Object.assign(
        this.style.ellipsis,
        this.styleConfig.ellipsis,
      );
      Object.assign(
        this.style.pages,
        this.styleConfig.pages,
      );
    },
    computed: {
      displayedPages: function () {
        const displayedFirstPage = (
          this.page - this.minimumDisplayedPageCount
        );
        const displayedLastPage = (
          this.page + this.minimumDisplayedPageCount
        );
        const leftOverflow = Math.max(this.firstPage - displayedFirstPage, 0);
        const rightOverflow = Math.max(displayedLastPage - this.lastPage, 0);

        const firstPage = Math.max(
          this.firstPage, displayedFirstPage - rightOverflow
        );
        const lastPage = Math.min(
          this.lastPage, displayedLastPage + leftOverflow
        );

        const displayedPages = (
          Array(lastPage - firstPage + 1).fill(0).map((_, i) => firstPage + i)
        );

        if (
          this.fixFirstPageButton &&
          displayedPages[0] !== this.firstPage
        ) {
          displayedPages[0] = this.firstPage;
          if (this.minimumDisplayedPageCount - leftOverflow > 1) {
            displayedPages[1] = null;
          }
        }

        if (
          this.fixLastPageButton &&
          displayedPages[displayedPages.length - 1] !== this.lastPage
        ) {
          displayedPages[displayedPages.length - 1] = this.lastPage;
          if (this.minimumDisplayedPageCount - rightOverflow > 1) {
            displayedPages[displayedPages.length - 2] = null;
          }
        }

        return displayedPages;
      },
      firstPage: function () {
        return this.pageStartsFromZero ? 0 : 1;
      },
      lastPage: function () {
        return this.pageStartsFromZero ? this.pageCount - 1 : this.pageCount;
      },
      previousPageExists: function () {
        return this.page - 1 >= this.firstPage;
      },
      nextPageExists: function () {
        return this.page + 1 <= this.lastPage;
      },
      previousPage: function () {
        return this.previousPageExists ? this.page - 1 : this.page;
      },
      nextPage: function () {
        return this.nextPageExists ? this.page + 1 : this.page;
      },
      pages: function () {
        return Array(this.pageCount).fill(0).map(
          (_, i) => this.pageStartsFromZero ? i : i + 1
        );
      },
    },
    methods: {
      selectPage: function (page) {
        if (
          this.disabled ||
          this.page === page
        ) {
          return;
        }

        this.page = page;
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.page = newVal;
      },
      page: function (newVal, oldVal) {
        this.$emit('change', this.page);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--v2-base-pagination-container-background-color)
    border var(--v2-base-pagination-container-border)
    border-radius var(--v2-base-pagination-container-border-radius)
    border-width var(--v2-base-pagination-container-border-width)
    box-shadow var(--v2-base-pagination-container-box-shadow)
    &[disabled]
      background-color var(--v2-base-pagination-container-background-color-disabled)
      border var(--v2-base-pagination-container-border-disabled)
      border-radius var(--v2-base-pagination-container-border-radius-disabled)
      border-width var(--v2-base-pagination-container-border-width-disabled)
      box-shadow var(--v2-base-pagination-container-box-shadow-disabled)

  .ellipsis
    background-color var(--v2-base-pagination-ellipsis-background-color)
    border var(--v2-base-pagination-ellipsis-border)
    border-radius var(--v2-base-pagination-ellipsis-border-radius)
    border-width var(--v2-base-pagination-ellipsis-border-width)
    box-shadow var(--v2-base-pagination-ellipsis-box-shadow)
    font-family var(--v2-base-pagination-ellipsis-font-family)
    font-size var(--v2-base-pagination-ellipsis-font-size)
    font-weight var(--v2-base-pagination-ellipsis-font-weight)
    &[disabled]
      background-color var(--v2-base-pagination-ellipsis-background-color-disabled)
      border var(--v2-base-pagination-ellipsis-border-disabled)
      border-radius var(--v2-base-pagination-ellipsis-border-radius-disabled)
      border-width var(--v2-base-pagination-ellipsis-border-width-disabled)
      box-shadow var(--v2-base-pagination-ellipsis-box-shadow-disabled)
      font-family var(--v2-base-pagination-ellipsis-font-family-disabled)
      font-size var(--v2-base-pagination-ellipsis-font-size-disabled)
      font-weight var(--v2-base-pagination-ellipsis-font-weight-disabled)

  .firstPageButton
    background-color var(--v2-base-pagination-first-page-button-background-color)
    border var(--v2-base-pagination-first-page-button-border)
    border-radius var(--v2-base-pagination-first-page-button-border-radius)
    border-width var(--v2-base-pagination-first-page-button-border-width)
    box-shadow var(--v2-base-pagination-first-page-button-box-shadow)
    color var(--v2-base-pagination-first-page-button-color)
    cursor var(--v2-base-pagination-first-page-button-cursor)
    font-family var(--v2-base-pagination-first-page-button-font-family)
    font-size var(--v2-base-pagination-first-page-button-font-size)
    font-weight var(--v2-base-pagination-first-page-button-font-weight)
    &[selected], &[selected]:hover, &[selected]:active
      background-color var(--v2-base-pagination-first-page-button-background-color-selected)
      border var(--v2-base-pagination-first-page-button-border-selected)
      border-radius var(--v2-base-pagination-first-page-button-border-radius-selected)
      border-width var(--v2-base-pagination-first-page-button-border-width-selected)
      box-shadow var(--v2-base-pagination-first-page-button-box-shadow-selected)
      color var(--v2-base-pagination-first-page-button-color-selected)
      cursor var(--v2-base-pagination-first-page-button-cursor-selected)
      font-family var(--v2-base-pagination-first-page-button-font-family-selected)
      font-size var(--v2-base-pagination-first-page-button-font-size-selected)
      font-weight var(--v2-base-pagination-first-page-button-font-weight-selected)
    &:hover
      background-color var(--v2-base-pagination-first-page-button-background-color-hover)
      border var(--v2-base-pagination-first-page-button-border-hover)
      border-radius var(--v2-base-pagination-first-page-button-border-radius-hover)
      border-width var(--v2-base-pagination-first-page-button-border-width-hover)
      box-shadow var(--v2-base-pagination-first-page-button-box-shadow-hover)
      color var(--v2-base-pagination-first-page-button-color-hover)
      cursor var(--v2-base-pagination-first-page-button-cursor-hover)
      font-family var(--v2-base-pagination-first-page-button-font-family-hover)
      font-size var(--v2-base-pagination-first-page-button-font-size-hover)
      font-weight var(--v2-base-pagination-first-page-button-font-weight-hover)
    &:active
      background-color var(--v2-base-pagination-first-page-button-background-color-active)
      border var(--v2-base-pagination-first-page-button-border-active)
      border-radius var(--v2-base-pagination-first-page-button-border-radius-active)
      border-width var(--v2-base-pagination-first-page-button-border-width-active)
      box-shadow var(--v2-base-pagination-first-page-button-box-shadow-active)
      color var(--v2-base-pagination-first-page-button-color-hover-active)
      cursor var(--v2-base-pagination-first-page-button-cursor-active)
      font-family var(--v2-base-pagination-first-page-button-font-family-active)
      font-size var(--v2-base-pagination-first-page-button-font-size-active)
      font-weight var(--v2-base-pagination-first-page-button-font-weight-active)
    &[disabled]
      background-color var(--v2-base-pagination-first-page-button-background-color-disabled)
      border var(--v2-base-pagination-first-page-button-border-disabled)
      border-radius var(--v2-base-pagination-first-page-button-border-radius-disabled)
      border-width var(--v2-base-pagination-first-page-button-border-width-disabled)
      box-shadow var(--v2-base-pagination-first-page-button-box-shadow-disabled)
      color var(--v2-base-pagination-first-page-button-color-disabled)
      cursor var(--v2-base-pagination-first-page-button-cursor-disabled)
      font-family var(--v2-base-pagination-first-page-button-font-family-disabled)
      font-size var(--v2-base-pagination-first-page-button-font-size-disabled)
      font-weight var(--v2-base-pagination-first-page-button-font-weight-disabled)

  .lastPageButton
    background-color var(--v2-base-pagination-last-page-button-background-color)
    border var(--v2-base-pagination-last-page-button-border)
    border-radius var(--v2-base-pagination-last-page-button-border-radius)
    border-width var(--v2-base-pagination-last-page-button-border-width)
    box-shadow var(--v2-base-pagination-last-page-button-box-shadow)
    color var(--v2-base-pagination-last-page-button-color)
    cursor var(--v2-base-pagination-last-page-button-cursor)
    font-family var(--v2-base-pagination-last-page-button-font-family)
    font-size var(--v2-base-pagination-last-page-button-font-size)
    font-weight var(--v2-base-pagination-last-page-button-font-weight)
    &[selected], &[selected]:hover, &[selected]:active
      background-color var(--v2-base-pagination-last-page-button-background-color-selected)
      border var(--v2-base-pagination-last-page-button-border-selected)
      border-radius var(--v2-base-pagination-last-page-button-border-radius-selected)
      border-width var(--v2-base-pagination-last-page-button-border-width-selected)
      box-shadow var(--v2-base-pagination-last-page-button-box-shadow-selected)
      color var(--v2-base-pagination-last-page-button-color-selected)
      cursor var(--v2-base-pagination-last-page-button-cursor-selected)
      font-family var(--v2-base-pagination-last-page-button-font-family-selected)
      font-size var(--v2-base-pagination-last-page-button-font-size-selected)
      font-weight var(--v2-base-pagination-last-page-button-font-weight-selected)
    &:hover
      background-color var(--v2-base-pagination-last-page-button-background-color-hover)
      border var(--v2-base-pagination-last-page-button-border-hover)
      border-radius var(--v2-base-pagination-last-page-button-border-radius-hover)
      border-width var(--v2-base-pagination-last-page-button-border-width-hover)
      box-shadow var(--v2-base-pagination-last-page-button-box-shadow-hover)
      color var(--v2-base-pagination-last-page-button-color-hover)
      cursor var(--v2-base-pagination-last-page-button-cursor-hover)
      font-family var(--v2-base-pagination-last-page-button-font-family-hover)
      font-size var(--v2-base-pagination-last-page-button-font-size-hover)
      font-weight var(--v2-base-pagination-last-page-button-font-weight-hover)
    &:active
      background-color var(--v2-base-pagination-last-page-button-background-color-active)
      border var(--v2-base-pagination-last-page-button-border-active)
      border-radius var(--v2-base-pagination-last-page-button-border-radius-active)
      border-width var(--v2-base-pagination-last-page-button-border-width-active)
      box-shadow var(--v2-base-pagination-last-page-button-box-shadow-active)
      color var(--v2-base-pagination-last-page-button-color-hover-active)
      cursor var(--v2-base-pagination-last-page-button-cursor-active)
      font-family var(--v2-base-pagination-last-page-button-font-family-active)
      font-size var(--v2-base-pagination-last-page-button-font-size-active)
      font-weight var(--v2-base-pagination-last-page-button-font-weight-active)
    &[disabled]
      background-color var(--v2-base-pagination-last-page-button-background-color-disabled)
      border var(--v2-base-pagination-last-page-button-border-disabled)
      border-radius var(--v2-base-pagination-last-page-button-border-radius-disabled)
      border-width var(--v2-base-pagination-last-page-button-border-width-disabled)
      box-shadow var(--v2-base-pagination-last-page-button-box-shadow-disabled)
      color var(--v2-base-pagination-last-page-button-color-disabled)
      cursor var(--v2-base-pagination-last-page-button-cursor-disabled)
      font-family var(--v2-base-pagination-last-page-button-font-family-disabled)
      font-size var(--v2-base-pagination-last-page-button-font-size-disabled)
      font-weight var(--v2-base-pagination-last-page-button-font-weight-disabled)

  .nextPageButton
    background-color var(--v2-base-pagination-next-page-button-background-color)
    border var(--v2-base-pagination-next-page-button-border)
    border-radius var(--v2-base-pagination-next-page-button-border-radius)
    border-width var(--v2-base-pagination-next-page-button-border-width)
    box-shadow var(--v2-base-pagination-next-page-button-box-shadow)
    color var(--v2-base-pagination-next-page-button-color)
    cursor var(--v2-base-pagination-next-page-button-cursor)
    font-family var(--v2-base-pagination-next-page-button-font-family)
    font-size var(--v2-base-pagination-next-page-button-font-size)
    font-weight var(--v2-base-pagination-next-page-button-font-weight)
    &[selected], &[selected]:hover, &[selected]:active
      background-color var(--v2-base-pagination-next-page-button-background-color-selected)
      border var(--v2-base-pagination-next-page-button-border-selected)
      border-radius var(--v2-base-pagination-next-page-button-border-radius-selected)
      border-width var(--v2-base-pagination-next-page-button-border-width-selected)
      box-shadow var(--v2-base-pagination-next-page-button-box-shadow-selected)
      color var(--v2-base-pagination-next-page-button-color-selected)
      cursor var(--v2-base-pagination-next-page-button-cursor-selected)
      font-family var(--v2-base-pagination-next-page-button-font-family-selected)
      font-size var(--v2-base-pagination-next-page-button-font-size-selected)
      font-weight var(--v2-base-pagination-next-page-button-font-weight-selected)
    &:hover
      background-color var(--v2-base-pagination-next-page-button-background-color-hover)
      border var(--v2-base-pagination-next-page-button-border-hover)
      border-radius var(--v2-base-pagination-next-page-button-border-radius-hover)
      border-width var(--v2-base-pagination-next-page-button-border-width-hover)
      box-shadow var(--v2-base-pagination-next-page-button-box-shadow-hover)
      color var(--v2-base-pagination-next-page-button-color-hover)
      cursor var(--v2-base-pagination-next-page-button-cursor-hover)
      font-family var(--v2-base-pagination-next-page-button-font-family-hover)
      font-size var(--v2-base-pagination-next-page-button-font-size-hover)
      font-weight var(--v2-base-pagination-next-page-button-font-weight-hover)
    &:active
      background-color var(--v2-base-pagination-next-page-button-background-color-active)
      border var(--v2-base-pagination-next-page-button-border-active)
      border-radius var(--v2-base-pagination-next-page-button-border-radius-active)
      border-width var(--v2-base-pagination-next-page-button-border-width-active)
      box-shadow var(--v2-base-pagination-next-page-button-box-shadow-active)
      color var(--v2-base-pagination-next-page-button-color-hover-active)
      cursor var(--v2-base-pagination-next-page-button-cursor-active)
      font-family var(--v2-base-pagination-next-page-button-font-family-active)
      font-size var(--v2-base-pagination-next-page-button-font-size-active)
      font-weight var(--v2-base-pagination-next-page-button-font-weight-active)
    &[disabled]
      background-color var(--v2-base-pagination-next-page-button-background-color-disabled)
      border var(--v2-base-pagination-next-page-button-border-disabled)
      border-radius var(--v2-base-pagination-next-page-button-border-radius-disabled)
      border-width var(--v2-base-pagination-next-page-button-border-width-disabled)
      box-shadow var(--v2-base-pagination-next-page-button-box-shadow-disabled)
      color var(--v2-base-pagination-next-page-button-color-disabled)
      cursor var(--v2-base-pagination-next-page-button-cursor-disabled)
      font-family var(--v2-base-pagination-next-page-button-font-family-disabled)
      font-size var(--v2-base-pagination-next-page-button-font-size-disabled)
      font-weight var(--v2-base-pagination-next-page-button-font-weight-disabled)

  .pageButton
    background-color var(--v2-base-pagination-page-button-background-color)
    border var(--v2-base-pagination-page-button-border)
    border-radius var(--v2-base-pagination-page-button-border-radius)
    border-width var(--v2-base-pagination-page-button-border-width)
    box-shadow var(--v2-base-pagination-page-button-box-shadow)
    color var(--v2-base-pagination-page-button-color)
    cursor var(--v2-base-pagination-page-button-cursor)
    font-family var(--v2-base-pagination-page-button-font-family)
    font-size var(--v2-base-pagination-page-button-font-size)
    font-weight var(--v2-base-pagination-page-button-font-weight)
    &[selected], &[selected]:hover, &[selected]:active
      background-color var(--v2-base-pagination-page-button-background-color-selected)
      border var(--v2-base-pagination-page-button-border-selected)
      border-radius var(--v2-base-pagination-page-button-border-radius-selected)
      border-width var(--v2-base-pagination-page-button-border-width-selected)
      box-shadow var(--v2-base-pagination-page-button-box-shadow-selected)
      color var(--v2-base-pagination-page-button-color-selected)
      cursor var(--v2-base-pagination-page-button-cursor-selected)
      font-family var(--v2-base-pagination-page-button-font-family-selected)
      font-size var(--v2-base-pagination-page-button-font-size-selected)
      font-weight var(--v2-base-pagination-page-button-font-weight-selected)
    &:hover
      background-color var(--v2-base-pagination-page-button-background-color-hover)
      border var(--v2-base-pagination-page-button-border-hover)
      border-radius var(--v2-base-pagination-page-button-border-radius-hover)
      border-width var(--v2-base-pagination-page-button-border-width-hover)
      box-shadow var(--v2-base-pagination-page-button-box-shadow-hover)
      color var(--v2-base-pagination-page-button-color-hover)
      cursor var(--v2-base-pagination-page-button-cursor-hover)
      font-family var(--v2-base-pagination-page-button-font-family-hover)
      font-size var(--v2-base-pagination-page-button-font-size-hover)
      font-weight var(--v2-base-pagination-page-button-font-weight-hover)
    &:active
      background-color var(--v2-base-pagination-page-button-background-color-active)
      border var(--v2-base-pagination-page-button-border-active)
      border-radius var(--v2-base-pagination-page-button-border-radius-active)
      border-width var(--v2-base-pagination-page-button-border-width-active)
      box-shadow var(--v2-base-pagination-page-button-box-shadow-active)
      color var(--v2-base-pagination-page-button-color-hover-active)
      cursor var(--v2-base-pagination-page-button-cursor-active)
      font-family var(--v2-base-pagination-page-button-font-family-active)
      font-size var(--v2-base-pagination-page-button-font-size-active)
      font-weight var(--v2-base-pagination-page-button-font-weight-active)
    &[disabled]
      background-color var(--v2-base-pagination-page-button-background-color-disabled)
      border var(--v2-base-pagination-page-button-border-disabled)
      border-radius var(--v2-base-pagination-page-button-border-radius-disabled)
      border-width var(--v2-base-pagination-page-button-border-width-disabled)
      box-shadow var(--v2-base-pagination-page-button-box-shadow-disabled)
      color var(--v2-base-pagination-page-button-color-disabled)
      cursor var(--v2-base-pagination-page-button-cursor-disabled)
      font-family var(--v2-base-pagination-page-button-font-family-disabled)
      font-size var(--v2-base-pagination-page-button-font-size-disabled)
      font-weight var(--v2-base-pagination-page-button-font-weight-disabled)

  .previousPageButton
    background-color var(--v2-base-pagination-previous-page-button-background-color)
    border var(--v2-base-pagination-previous-page-button-border)
    border-radius var(--v2-base-pagination-previous-page-button-border-radius)
    border-width var(--v2-base-pagination-previous-page-button-border-width)
    box-shadow var(--v2-base-pagination-previous-page-button-box-shadow)
    color var(--v2-base-pagination-previous-page-button-color)
    cursor var(--v2-base-pagination-previous-page-button-cursor)
    font-family var(--v2-base-pagination-previous-page-button-font-family)
    font-size var(--v2-base-pagination-previous-page-button-font-size)
    font-weight var(--v2-base-pagination-previous-page-button-font-weight)
    &[selected], &[selected]:hover, &[selected]:active
      background-color var(--v2-base-pagination-previous-page-button-background-color-selected)
      border var(--v2-base-pagination-previous-page-button-border-selected)
      border-radius var(--v2-base-pagination-previous-page-button-border-radius-selected)
      border-width var(--v2-base-pagination-previous-page-button-border-width-selected)
      box-shadow var(--v2-base-pagination-previous-page-button-box-shadow-selected)
      color var(--v2-base-pagination-previous-page-button-color-selected)
      cursor var(--v2-base-pagination-previous-page-button-cursor-selected)
      font-family var(--v2-base-pagination-previous-page-button-font-family-selected)
      font-size var(--v2-base-pagination-previous-page-button-font-size-selected)
      font-weight var(--v2-base-pagination-previous-page-button-font-weight-selected)
    &:hover
      background-color var(--v2-base-pagination-previous-page-button-background-color-hover)
      border var(--v2-base-pagination-previous-page-button-border-hover)
      border-radius var(--v2-base-pagination-previous-page-button-border-radius-hover)
      border-width var(--v2-base-pagination-previous-page-button-border-width-hover)
      box-shadow var(--v2-base-pagination-previous-page-button-box-shadow-hover)
      color var(--v2-base-pagination-previous-page-button-color-hover)
      cursor var(--v2-base-pagination-previous-page-button-cursor-hover)
      font-family var(--v2-base-pagination-previous-page-button-font-family-hover)
      font-size var(--v2-base-pagination-previous-page-button-font-size-hover)
      font-weight var(--v2-base-pagination-previous-page-button-font-weight-hover)
    &:active
      background-color var(--v2-base-pagination-previous-page-button-background-color-active)
      border var(--v2-base-pagination-previous-page-button-border-active)
      border-radius var(--v2-base-pagination-previous-page-button-border-radius-active)
      border-width var(--v2-base-pagination-previous-page-button-border-width-active)
      box-shadow var(--v2-base-pagination-previous-page-button-box-shadow-active)
      color var(--v2-base-pagination-previous-page-button-color-hover-active)
      cursor var(--v2-base-pagination-previous-page-button-cursor-active)
      font-family var(--v2-base-pagination-previous-page-button-font-family-active)
      font-size var(--v2-base-pagination-previous-page-button-font-size-active)
      font-weight var(--v2-base-pagination-previous-page-button-font-weight-active)
    &[disabled]
      background-color var(--v2-base-pagination-previous-page-button-background-color-disabled)
      border var(--v2-base-pagination-previous-page-button-border-disabled)
      border-radius var(--v2-base-pagination-previous-page-button-border-radius-disabled)
      border-width var(--v2-base-pagination-previous-page-button-border-width-disabled)
      box-shadow var(--v2-base-pagination-previous-page-button-box-shadow-disabled)
      color var(--v2-base-pagination-previous-page-button-color-disabled)
      cursor var(--v2-base-pagination-previous-page-button-cursor-disabled)
      font-family var(--v2-base-pagination-previous-page-button-font-family-disabled)
      font-size var(--v2-base-pagination-previous-page-button-font-size-disabled)
      font-weight var(--v2-base-pagination-previous-page-button-font-weight-disabled)
</style>
